import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, graphqlOperation, Auth } from "aws-amplify";
import {
  getCompanyProfile,
  listCompanyProfiles,
  listCompanySubscriptions,
  listPrivateScreenings,
} from "../../graphql/queries";
import {
  updateCompanyProfile,
  updateCompanySubscription,
} from "../../graphql/mutations";
import Header from "../../components/header/Header";

const CompanyProfile = () => {
  const navigate = useNavigate();
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [totalScans, setTotalScans] = useState(0);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [packageHistory, setPackageHistory] = useState([]);
  const [showUpcomingPackage, setShowUpcomingPackage] = useState(false);

  const companyName = companyProfile?.companyName || "";
  const companyEmail = companyProfile?.email || "";
  const companyUrl = companyProfile?.companyUrl || "";
  const contactPersonName = companyProfile?.contactPersonName || "";
  const contactPersonDesignation = companyProfile?.contactPersonDesignation || "";
  const contactPersonNumber = companyProfile?.contactPersonNumber || "";

  const currentPackage = useMemo(() => {
    const planName = companyProfile?.CompanySubscriptionPlan || "Trial";
    const start = companyProfile?.CompanySubscriptionStartDate
      ? new Date(companyProfile.CompanySubscriptionStartDate).toLocaleDateString()
      : "";
    const end = companyProfile?.CompanySubscriptionEndDate
      ? new Date(companyProfile.CompanySubscriptionEndDate).toLocaleDateString()
      : "";
    return { name: planName, startDate: start, endDate: end };
  }, [companyProfile]);

  const currentPoints = companyProfile?.remainingCredit ?? 0;

  useEffect(() => {
    const resolveCompanyProfileId = async () => {
      const storedId = localStorage.getItem("CompanyProfileID");
      if (storedId) return storedId;

      const storedCompanyProfile = localStorage.getItem("CompanyProfile");
      if (storedCompanyProfile) {
        try {
          const parsed = JSON.parse(storedCompanyProfile);
          if (parsed?.id) return parsed.id;
        } catch (e) {
          // ignore
        }
      }

      // Last resort: lookup by authenticated email (or candidateProfileEmailID)
      let emailToUse = null;
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        emailToUse = currentUser?.attributes?.email || null;
      } catch (e) {
        // ignore
      }
      emailToUse = emailToUse || localStorage.getItem("candidateProfileEmailID");
      if (!emailToUse) return null;

      const listRes = await API.graphql(
        graphqlOperation(listCompanyProfiles, {
          filter: { email: { eq: emailToUse } },
          limit: 1,
        })
      );

      const found = listRes?.data?.listCompanyProfiles?.items?.[0];
      if (found?.id) {
        localStorage.setItem("CompanyProfileID", found.id);
        localStorage.setItem("CompanyEmailID", found.email || emailToUse);
        localStorage.setItem("CompanyProfile", JSON.stringify(found));
        return found.id;
      }
      return null;
    };

    const fetchAll = async () => {
      setLoading(true);
      setLoadError("");
      try {
        // Prefer cached profile for immediate display
        const cached = localStorage.getItem("CompanyProfile");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed?.id) setCompanyProfile(parsed);
          } catch (e) {
            // ignore
          }
        }

        const companyProfileID = await resolveCompanyProfileId();
        if (!companyProfileID) {
          setLoadError("Company profile not found for this account.");
          return;
        }

        const response = await API.graphql(
          graphqlOperation(getCompanyProfile, { id: companyProfileID })
        );
        const profile = response?.data?.getCompanyProfile || null;
        if (profile) {
          setCompanyProfile(profile);
          localStorage.setItem("CompanyProfileID", profile.id);
          if (profile.email) localStorage.setItem("CompanyEmailID", profile.email);
          localStorage.setItem("CompanyProfile", JSON.stringify(profile));
        }

        const screeningsResponse = await API.graphql(
          graphqlOperation(listPrivateScreenings, {
            filter: { companyProfileID: { eq: companyProfileID } },
            limit: 1000,
          })
        );
        const screenings = screeningsResponse?.data?.listPrivateScreenings?.items || [];
        setTotalScans(screenings.length);

        const transactions = screenings
          .slice()
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .map((screening) => ({
            date: screening.createdAt ? new Date(screening.createdAt).toLocaleString() : "",
            name: screening.candidateName || "",
            email: screening.candidateEmail || "",
            deduction: screening.pointsDeducted ?? "",
          }));
        setTransactionHistory(transactions);

        const subscriptionRes = await API.graphql(
          graphqlOperation(listCompanySubscriptions, {
            filter: { companyProfileID: { eq: companyProfileID } },
            limit: 1000,
          })
        );
        const packages = subscriptionRes?.data?.listCompanySubscriptions?.items || [];
        packages.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setPackageHistory(packages);
      } catch (error) {
        console.error("Error fetching company profile:", error);
        setLoadError("Failed to load company profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const handleButtonClick = (isUpcoming) => {
    setShowUpcomingPackage(isUpcoming);
  };

  const handleActivatePackage = async (pkg) => {
    const confirmation = window.confirm(
      "Are you sure you want to activate this package?"
    );
    if (!confirmation) return;

    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + parseInt(pkg.duration));

    const companyProfileID = localStorage.getItem("CompanyProfileID");

    try {
      await API.graphql(
        graphqlOperation(updateCompanyProfile, {
          input: {
            id: companyProfileID,
            CompanySubscriptionStartDate: startDate.toISOString(),
            CompanySubscriptionEndDate: endDate.toISOString(),
          },
        })
      );

      await API.graphql(
        graphqlOperation(updateCompanySubscription, {
          input: {
            id: pkg.id,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        })
      );

      alert("Package activated successfully!");
    } catch (error) {
      console.error("Error activating package:", error);
      alert("Failed to activate the package. Please try again.");
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      fontFamily: 'Sora, Inter, sans-serif',
      background: '#0f172a',
      color: '#F9FAFB'
    }}>
      <Header />

      {/* Background Effect */}
      <div className="fixed inset-0" style={{ zIndex: 0, pointerEvents: 'none' }}>
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.6) 100%)'
          }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto py-8 px-6 sm:px-8">
        {/* Back to Dashboard */}
        <button
          onClick={() => navigate('/JobDetailsList')}
          className="group flex items-center gap-2 text-slate-400 hover:text-indigo-400 transition-colors mb-6"
        >
          <div className="w-9 h-9 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center group-hover:border-indigo-500/30 group-hover:bg-indigo-500/10 transition-all">
            <svg className="w-4 h-4 transform group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </div>
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        {loading && (
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 backdrop-blur-md p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-slate-600 border-t-indigo-500"></div>
            <p className="text-slate-400 mt-4 text-sm">Loading profile…</p>
          </div>
        )}

        {loadError && !loading && (
          <div className="rounded-xl border border-red-900/50 bg-red-950/30 p-4 flex items-start gap-3">
            <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '20px' }} className="text-red-400">error</span>
            <p className="m-0 text-red-200 text-sm">{loadError}</p>
          </div>
        )}

        {!loadError && !loading && (
          <>
            {/* Hero / Identity card — 2 rows: identity on top, contact chips below */}
            <div
              className="rounded-2xl p-6 mb-6 relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.18) 0%, rgba(124, 58, 237, 0.14) 50%, rgba(30, 41, 59, 0.5) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                backdropFilter: 'blur(14px)'
              }}
            >
              {/* Row 1: avatar + name + plan badge */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0 shadow-lg"
                     style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}>
                  {(companyName || 'C').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-indigo-300/80 font-semibold mb-0.5">Company</div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white m-0 tracking-tight truncate" style={{ fontFamily: 'Sora, sans-serif' }}>
                    {companyName || 'Company Profile'}
                  </h1>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-200 border border-indigo-500/40 whitespace-nowrap">
                    {currentPackage.name}
                  </span>
                  {currentPackage.startDate && currentPackage.endDate && (
                    <span className="text-[11px] text-slate-500 whitespace-nowrap">
                      {currentPackage.startDate} → {currentPackage.endDate}
                    </span>
                  )}
                </div>
              </div>

              {/* Row 2: contact chips (only render if there's at least one) */}
              {(companyEmail || companyUrl) && (
                <div className="flex flex-wrap items-center gap-2 mt-4 pl-[72px]">
                  {companyEmail && (
                    <a
                      href={`mailto:${companyEmail}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-300 hover:text-white transition-colors max-w-full"
                      style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(71, 85, 105, 0.4)' }}
                      title={companyEmail}
                    >
                      <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }} className="text-indigo-300 flex-shrink-0">mail</span>
                      <span className="truncate">{companyEmail}</span>
                    </a>
                  )}
                  {companyUrl && (
                    <a
                      href={companyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-300 hover:text-white transition-colors max-w-full"
                      style={{ background: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(71, 85, 105, 0.4)' }}
                      title={companyUrl}
                    >
                      <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '14px' }} className="text-purple-300 flex-shrink-0">language</span>
                      <span className="truncate">{companyUrl.replace(/^https?:\/\//, '')}</span>
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {[
                { label: 'Remaining Credits', value: currentPoints, icon: 'monetization_on', accent: 'text-indigo-300', iconBg: 'bg-indigo-500/15' },
                { label: 'Total Screenings', value: totalScans, icon: 'group', accent: 'text-purple-300', iconBg: 'bg-purple-500/15' },
                { label: 'Current Plan', value: currentPackage.name, icon: 'workspace_premium', accent: 'text-emerald-300', iconBg: 'bg-emerald-500/15', small: true },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="rounded-xl p-4 border border-slate-800"
                  style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(10px)' }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${stat.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '20px' }} className={stat.accent}>{stat.icon}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{stat.label}</div>
                      <div className={`${stat.small ? 'text-lg' : 'text-2xl'} font-bold text-white leading-tight truncate`}>{stat.value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Two-column info section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              {/* Company Information */}
              <div
                className="rounded-xl p-5 border border-slate-800"
                style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(10px)' }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '18px' }} className="text-indigo-400">business</span>
                  <h2 className="text-sm font-semibold text-white m-0">Company Information</h2>
                </div>
                <dl className="space-y-3 m-0">
                  {[
                    { label: 'Company Name', value: companyName },
                    { label: 'Email Address', value: companyEmail, breakAll: true },
                    { label: 'Website', value: companyUrl, link: companyUrl, breakAll: true },
                  ].map((row) => (
                    <div key={row.label} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
                      <dt className="text-xs text-slate-500 sm:w-28 sm:flex-shrink-0 sm:pt-0.5">{row.label}</dt>
                      <dd className={`text-sm text-white font-medium m-0 flex-1 min-w-0 ${row.breakAll ? 'break-all' : ''}`}>
                        {row.value
                          ? row.link
                            ? <a href={row.link} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 transition-colors">{row.value}</a>
                            : row.value
                          : <span className="text-slate-600">—</span>}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              {/* Contact Person */}
              <div
                className="rounded-xl p-5 border border-slate-800"
                style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(10px)' }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '18px' }} className="text-purple-400">person</span>
                  <h2 className="text-sm font-semibold text-white m-0">Contact Person</h2>
                </div>
                <dl className="space-y-3 m-0">
                  {[
                    { label: 'Full Name', value: contactPersonName },
                    { label: 'Designation', value: contactPersonDesignation },
                    { label: 'Phone Number', value: contactPersonNumber },
                  ].map((row) => (
                    <div key={row.label} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
                      <dt className="text-xs text-slate-500 sm:w-28 sm:flex-shrink-0 sm:pt-0.5">{row.label}</dt>
                      <dd className="text-sm text-white font-medium m-0 flex-1 min-w-0">
                        {row.value || <span className="text-slate-600">—</span>}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            {/* Recent Screenings */}
            <div
              className="rounded-xl border border-slate-800 overflow-hidden"
              style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(10px)' }}
            >
              <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(55, 65, 81, 0.5)' }}>
                <div className="flex items-center gap-2">
                  <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '18px' }} className="text-indigo-400">history</span>
                  <h2 className="text-sm font-semibold text-white m-0">Recent Private Screenings</h2>
                </div>
                {transactionHistory.length > 0 && (
                  <span className="text-[11px] text-slate-500">{transactionHistory.length} total</span>
                )}
              </div>
              <div className="overflow-auto max-h-[420px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-900/95 backdrop-blur-sm">
                    <tr style={{ borderBottom: '1px solid rgba(55, 65, 81, 0.5)' }}>
                      <th className="px-5 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-slate-500">Date</th>
                      <th className="px-5 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-slate-500">Candidate</th>
                      <th className="px-5 py-2.5 text-left font-semibold text-[11px] uppercase tracking-wider text-slate-500">Email</th>
                      <th className="px-5 py-2.5 text-right font-semibold text-[11px] uppercase tracking-wider text-slate-500">Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactionHistory.length === 0 ? (
                      <tr>
                        <td className="px-5 py-12 text-center text-slate-500" colSpan={4}>
                          <span style={{ fontFamily: 'Material Symbols Outlined', fontSize: '36px' }} className="text-slate-700 block mb-2">inbox</span>
                          <p className="m-0 text-sm">No screenings yet</p>
                          <p className="m-0 text-xs text-slate-600 mt-1">Invite a candidate from the dashboard to get started</p>
                        </td>
                      </tr>
                    ) : (
                      transactionHistory.slice(0, 20).map((txn, index) => (
                        <tr key={index} className="hover:bg-slate-800/30 transition-colors" style={{ borderBottom: '1px solid rgba(55, 65, 81, 0.3)' }}>
                          <td className="px-5 py-3 text-slate-400 text-xs whitespace-nowrap">{txn.date}</td>
                          <td className="px-5 py-3 text-white font-medium">{txn.name || <span className="text-slate-600">—</span>}</td>
                          <td className="px-5 py-3 text-slate-400 break-all">{txn.email || <span className="text-slate-600">—</span>}</td>
                          <td className="px-5 py-3 text-right">
                            {txn.deduction !== "" ? (
                              <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-indigo-500/15 text-indigo-300">
                                −{txn.deduction}
                              </span>
                            ) : (
                              <span className="text-slate-600">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyProfile;
